// ----------------------------------------------------------------------

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
  MANAGEMENT: '/management',
  CLOUD_COMPASS: '/cloud-compass',
  SETTING: '/settings',
  LEGAL: '/legal',
};

// ----------------------------------------------------------------------

export const paths = {
  minimalUI: 'https://mui.com/store/items/minimal-dashboard/',
  // AUTH
  auth: {
    jwt: {
      login: `${ROOTS.AUTH}/jwt/login`,
      register: `${ROOTS.AUTH}/jwt/register`,
    },
  },
  // MANAGEMENT
  management: {
    root: ROOTS.MANAGEMENT,
    accounts: {
      root: `${ROOTS.MANAGEMENT}/accounts`,

      organizations: {
        root: `${ROOTS.MANAGEMENT}/accounts/organizations`,
        new: `${ROOTS.MANAGEMENT}/accounts/organizations/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/edit`,

        overview: (id: string) => `${ROOTS.MANAGEMENT}/accounts/organizations/${id}`,

        tab: (id: string, tab: string) => `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/${tab}`,
        subTab: (id: string, tab: string, subTab: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/${tab}/${subTab}`,
        tabInformationEdit: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/information/edit`,
        tabInformation: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/information`,

        tabAddDomain: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/digital-assets/domains/new`,
        tabEditDomain: (id: string, domain: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/digital-assets/domains/${domain}/edit`,
        tabCloudAccount: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/digital-assets/accounts`,

        tabAddContact: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/contacts/new`,
        tabEditContact: (id: string, contact: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/contacts/${contact}/edit`,
        tabAddDocument: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/documents/new`,
        tabEditDocument: (id: string, document: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/documents/${document}/edit`,

        subscriptions: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions`,
        tabAddSubscription: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/new`,
        tabEditSubscription: (id: string, subscription: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/edit`,

        tabAddOrder: (id: string) => `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/orders/new`,
        tabEditOrder: (id: string, order: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/orders/${order}/edit`,

        tabAddUser: (id: string) => `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/users/new`,
        tabEditUser: (id: string, user: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/users/${user}/edit`,

        tabInformationSubscription: (id: string, subscription: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/information`,

        tabSubscriptionAddonInformation: (id: string, subscription: string, addonId: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/addons/${addonId}/information`,

        tabViewOrders: (id: string, subscription: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/orders`,

        tabSubscriptionBilling: (id: string, subscription: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/billing`,

        tabSubscriptionAddons: (id: string, subscription: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/addons`,
        tabSubscriptionAddAddons: (id: string, subscription: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/addons/new`,
        tabSubscriptionEditAddons: (id: string, subscription: string, addonId: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/subscriptions/${subscription}/addons/${addonId}/edit`,

        tabAddEntity: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/legal-entities/new`,
        tabEditEntity: (id: string, legalEntity: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/legal-entities/${legalEntity}/edit`,
        consumptionBag: (id: string, bagId: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/balance/consumption-bags/${bagId}`,

        tabAddBillingOrder: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/billing/orders/new`,
        tabViewBillingOrder: (id: string, order: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/billing/${order}/information`,

        tabCancellationOrders: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/cancellation-orders`,

        tabOpportunities: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/organizations/${id}/opportunities`,
      },
      contacts: {
        root: `${ROOTS.MANAGEMENT}/accounts/contacts`,
        new: `${ROOTS.MANAGEMENT}/accounts/contacts/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/accounts/contacts/${id}/edit`,
        overview: (id: string) => `${ROOTS.MANAGEMENT}/accounts/contacts/${id}`,
        information: (id: string) => `${ROOTS.MANAGEMENT}/accounts/contacts/${id}/information`,
        activities: (id: string) => `${ROOTS.MANAGEMENT}/accounts/contacts/${id}/activities`,
        tab: (id: string, tab: string) => `${ROOTS.MANAGEMENT}/accounts/contacts/${id}/${tab}`,
      },
      subscriptions: {
        root: `${ROOTS.MANAGEMENT}/accounts/subscriptions`,
        new: `${ROOTS.MANAGEMENT}/accounts/subscriptions/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/accounts/subscriptions/${id}/edit`,
        information: (id: string) => `${ROOTS.MANAGEMENT}/accounts/subscriptions/${id}/information`,
        orders: (id: string) => `${ROOTS.MANAGEMENT}/accounts/subscriptions/${id}/orders`,
        addons: {
          root: (id: string) => `${ROOTS.MANAGEMENT}/accounts/subscriptions/${id}/addons`,
          tabAddons: (id: string, addonId: string, tab: string) =>
            `${ROOTS.MANAGEMENT}/accounts/subscriptions/${id}/addons/${addonId}/${tab}`,

          new: (id: string) => `${ROOTS.MANAGEMENT}/accounts/subscriptions/${id}/addons/new`,
          information: (id: string, addonId: string) =>
            `${ROOTS.MANAGEMENT}/accounts/subscriptions/${id}/addons/${addonId}/information`,
          edit: (subscriptionId: string, addonId: string) =>
            `${ROOTS.MANAGEMENT}/accounts/subscriptions/${subscriptionId}/addons/${addonId}/edit`,
        },
      },
      orders: {
        root: `${ROOTS.MANAGEMENT}/accounts/orders`,
        new: `${ROOTS.MANAGEMENT}/accounts/orders/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/accounts/orders/${id}/edit`,
        information: (id: string) => `${ROOTS.MANAGEMENT}/accounts/orders/${id}/information`,
      },
      billingOrders: {
        root: `${ROOTS.MANAGEMENT}/accounts/orders/billing`,
        new: `${ROOTS.MANAGEMENT}/accounts/orders/billing/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/accounts/orders/billing/${id}/edit`,
        information: (id: string) =>
          `${ROOTS.MANAGEMENT}/accounts/orders/billing/${id}/information`,
      },
      opportunities: {
        root: `${ROOTS.MANAGEMENT}/accounts/opportunities`,
        new: `${ROOTS.MANAGEMENT}/accounts/opportunities/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/accounts/opportunities/${id}/edit`,
        detail: (id: string) => `${ROOTS.MANAGEMENT}/accounts/opportunities/${id}`,
        summary: `${ROOTS.MANAGEMENT}/accounts/opportunities/summary`,
      },
      presalesTickets: {
        root: `${ROOTS.MANAGEMENT}/accounts/presales-tickets`,
        list: `${ROOTS.MANAGEMENT}/accounts/presales-tickets/list`,
        detail: (id: string) => `${ROOTS.MANAGEMENT}/accounts/presales-tickets/${id}`,
      },
      activities: {
        root: `${ROOTS.MANAGEMENT}/accounts/activities`,
        overview: `${ROOTS.MANAGEMENT}/accounts/activities/overview`,
        myActivities: `${ROOTS.MANAGEMENT}/accounts/activities/my-activities`,
        all: `${ROOTS.MANAGEMENT}/accounts/activities/all`,
      },
      request: {
        root: `${ROOTS.MANAGEMENT}/accounts/orders/requests`,
        users: {
          root: `${ROOTS.MANAGEMENT}/accounts/orders/access`,
          detail: (id: string) => `${ROOTS.MANAGEMENT}/accounts/orders/access/${id}/detail`,
        },
        new: `${ROOTS.MANAGEMENT}/accounts/orders/requests/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/accounts/orders/requests/${id}/edit`,
        information: (id: string) => `${ROOTS.MANAGEMENT}/accounts/orders/requests/${id}/information`,
      },
    },
    portfolio: {
      root: `${ROOTS.MANAGEMENT}/portfolio`,
      products: {
        root: `${ROOTS.MANAGEMENT}/portfolio/products`,
        new: `${ROOTS.MANAGEMENT}/portfolio/products/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/products/${id}/edit`,
        information: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/products/${id}/information`,
        skus: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/products/${id}/skus`,
      },
      partners: {
        root: `${ROOTS.MANAGEMENT}/portfolio/partners`,
        new: `${ROOTS.MANAGEMENT}/portfolio/partners/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/edit`,

        notificationTab: (id: string) =>
          `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/notification`,

        informationTab: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/information`,
        informationTabEdit: (id: string) =>
          `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/information/edit`,
        tab: (id: string, tab: string) => `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/${tab}`,

        tabAddContact: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/contacts/new`,
        tabEditContact: (id: string, contact: string) =>
          `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/contacts/${contact}/edit`,
        tabAddProvider: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/consoles/new`,
        tabEditProvider: (id: string, provider: string) =>
          `${ROOTS.MANAGEMENT}/portfolio/partners/${id}/consoles/${provider}/edit`,
      },
      skus: {
        root: `${ROOTS.MANAGEMENT}/portfolio/skus`,
        new: `${ROOTS.MANAGEMENT}/portfolio/skus/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/skus/${id}/edit`,
        information: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/skus/${id}/information`,
        plans: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/skus/${id}/plans`,
      },
      plans: {
        root: `${ROOTS.MANAGEMENT}/portfolio/plans`,
        new: `${ROOTS.MANAGEMENT}/portfolio/plans/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/plans/${id}/edit`,
        information: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/plans/${id}/information`,
        subscription: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/plans/${id}/subscription`,
      },
      pricebook: {
        root: `${ROOTS.MANAGEMENT}/portfolio/pricebook`,
        new: `${ROOTS.MANAGEMENT}/portfolio/pricebook/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/portfolio/pricebook/${id}/edit`,
      },
    },
    reports: {
      root: `${ROOTS.MANAGEMENT}/reports`,
      forecast: `${ROOTS.MANAGEMENT}/reports/forecast`,
      subscriptions: `${ROOTS.MANAGEMENT}/reports/subscriptions`,
      revenue: `${ROOTS.MANAGEMENT}/reports/revenue`,
      sales: `${ROOTS.MANAGEMENT}/reports/sales`,
    },
    marketing: {
      root: `${ROOTS.MANAGEMENT}/marketing`,
      leads: {
        root: `${ROOTS.MANAGEMENT}/marketing/leads`,
      },
      lists: {
        root: `${ROOTS.MANAGEMENT}/marketing/lists`,
        new: `${ROOTS.MANAGEMENT}/marketing/lists/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/marketing/lists/${id}/edit`,
        view: (id: string) => `${ROOTS.MANAGEMENT}/marketing/lists/${id}`,
      },
      emails: {
        root: `${ROOTS.MANAGEMENT}/marketing/emails`,
        new: `${ROOTS.MANAGEMENT}/marketing/emails/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/marketing/emails/${id}/edit`,
        view: (id: string) => `${ROOTS.MANAGEMENT}/marketing/emails/${id}`,
      },
      templates: {
        root: `${ROOTS.MANAGEMENT}/marketing/templates`,
        new: `${ROOTS.MANAGEMENT}/marketing/templates/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/marketing/templates/${id}/edit`,
        view: (id: string) => `${ROOTS.MANAGEMENT}/marketing/templates/${id}`,
      },
      forms: {
        root: `${ROOTS.MANAGEMENT}/marketing/forms`,
        new: `${ROOTS.MANAGEMENT}/marketing/forms/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/marketing/forms/${id}/edit`,
        view: (id: string) => `${ROOTS.MANAGEMENT}/marketing/forms/${id}`,
      },
      events: {
        root: `${ROOTS.MANAGEMENT}/marketing/events`,
        new: `${ROOTS.MANAGEMENT}/marketing/events/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/marketing/events/${id}/edit`,
        view: (id: string) => `${ROOTS.MANAGEMENT}/marketing/events/${id}`,
      },
      sequences: {
        root: `${ROOTS.MANAGEMENT}/marketing/sequences`,
        new: `${ROOTS.MANAGEMENT}/marketing/sequences/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/marketing/sequences/${id}/edit`,
        view: (id: string) => `${ROOTS.MANAGEMENT}/marketing/sequences/${id}`,
      },
    },
    settings: {
      root: `${ROOTS.MANAGEMENT}/settings`,
      // country: {
      //   root: `${ROOTS.MANAGEMENT}/settings/countries`,
      //   new: `${ROOTS.MANAGEMENT}/settings/countries/new`,
      //   edit: (id: string) => `${ROOTS.MANAGEMENT}/settings/countries/${id}/edit`,
      // },
      // industry: {
      //   root: `${ROOTS.MANAGEMENT}/settings/industries`,
      //   new: `${ROOTS.MANAGEMENT}/settings/industries/new`,
      //   edit: (id: string) => `${ROOTS.MANAGEMENT}/settings/industries/${id}/edit`,
      // },
      // segment: {
      //   root: `${ROOTS.MANAGEMENT}/settings/segments`,
      //   new: `${ROOTS.MANAGEMENT}/settings/segments/new`,
      //   edit: (id: string) => `${ROOTS.MANAGEMENT}/settings/segments/${id}/edit`,
      // },
      // lifeCycle: {
      //   root: `${ROOTS.MANAGEMENT}/settings/life-cycles`,
      //   new: `${ROOTS.MANAGEMENT}/settings/life-cycles/new`,
      //   edit: (id: string) => `${ROOTS.MANAGEMENT}/settings/life-cycles/${id}/edit`,
      // },
      // churnReason: {
      //   root: `${ROOTS.MANAGEMENT}/settings/churn-reasons`,
      //   new: `${ROOTS.MANAGEMENT}/settings/churn-reasons/new`,
      //   edit: (id: string) => `${ROOTS.MANAGEMENT}/settings/churn-reasons/${id}/edit`,
      // },
      accounts: {
        root: `${ROOTS.MANAGEMENT}/settings/accounts/general`,
        security: `${ROOTS.MANAGEMENT}/settings/accounts/security`,
        notifications: `${ROOTS.MANAGEMENT}/settings/accounts/notifications`,
        integrations: `${ROOTS.MANAGEMENT}/settings/accounts/integrations`,
        new: `${ROOTS.MANAGEMENT}/settings/churn-reasons/new`,
        edit: (id: string) => `${ROOTS.MANAGEMENT}/settings/churn-reasons/${id}/edit`,
      },
      storefront: {
        root: `${ROOTS.MANAGEMENT}/settings/storefront`,
      },
    },
    integrations: {
      root: `${ROOTS.MANAGEMENT}/integrations`,
      consoles: {
        root: `${ROOTS.MANAGEMENT}/integrations/consoles`,
      },
      apis: {
        root: `${ROOTS.MANAGEMENT}/integrations/apis`,
      },
    },
  },
  cloudCompass: {
    root: `${ROOTS.CLOUD_COMPASS}/cloud`,

    summary: {
      root: `${ROOTS.CLOUD_COMPASS}/summary`,
    },
    aws: {
      root: `${ROOTS.CLOUD_COMPASS}/cloud/aws`,
      accounts: {
        root: `${ROOTS.CLOUD_COMPASS}/cloud/aws/accounts`,
        payerAccounts: {
          root: `${ROOTS.CLOUD_COMPASS}/cloud/aws/accounts/payer-accounts`,
          new: `${ROOTS.CLOUD_COMPASS}/cloud/aws/accounts/payer-accounts/new`,
          edit: (id: string, backTo?: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/aws/accounts/payer-accounts/${id}/edit${backTo ? `?backTo=${backTo}` : ''}`,
          tab: (id: string, tab: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/aws/accounts/payer-accounts/${id}/${tab}`,

          linkedAccounts: {
            edit: (id: string, backTo?: string) =>
              `${ROOTS.CLOUD_COMPASS}/cloud/aws/accounts/payer-accounts/linked-accounts/${id}/edit${backTo ? `?backTo=${backTo}` : ''}`,
            information: (id: string) =>
              `${ROOTS.CLOUD_COMPASS}/cloud/aws/accounts/payer-accounts/linked-accounts/${id}/information`,
          },
        },
      },
    },
    gcp: {
      root: `${ROOTS.CLOUD_COMPASS}/cloud/gcp`,
      accounts: {
        root: `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts`,
        masterBillingAccounts: {
          root: `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts`,
          new: `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/new`,
          edit: (id: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/${id}/edit`,
          tab: (id: string, tab: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/${id}/${tab}`,
        },
        subAccounts: {
          root: `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/subaccounts`,
          tab: (id: string, tab: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/subaccounts/${id}/${tab}`,
          edit: (id: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/subaccounts/${id}/edit`,
        },
        projects: {
          root: `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/subaccounts/projects`,
          list: (id: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/gcp/accounts/billing-accounts/subaccounts/projects/${id}`,
        },
      },
    },
    azure: {
      root: `${ROOTS.CLOUD_COMPASS}/cloud/azure`,
      accounts: {
        root: `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts`,
        billingAccounts: {
          root: `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts/billing-accounts`,
          tab: (id: string, tab: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts/billing-accounts/${id}/${tab}`,
        },
        customers: {
          root: `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts/billing-accounts/customers`,
          tab: (id: string, tab: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts/billing-accounts/customers/${id}/${tab}`,
          edit: (id: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts/billing-accounts/customers/${id}/edit`,
        },
        subscriptions: {
          root: `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts/billing-accounts/customers/subscriptions`,
          list: (id: string) =>
            `${ROOTS.CLOUD_COMPASS}/cloud/azure/accounts/billing-accounts/customers/subscriptions/${id}`,
        },
      },
    },
    oci: {
      root: `${ROOTS.CLOUD_COMPASS}/cloud/oci`,
      tenancies: {
        root: `${ROOTS.CLOUD_COMPASS}/cloud/oci/tenancies`,
        // new: `${ROOTS.CLOUD_COMPASS}/cloud/oci/tenancies/new`,
        edit: (id: string) => `${ROOTS.CLOUD_COMPASS}/cloud/oci/tenancies/${id}/edit`,
        tab: (id: string, tab: string) => `${ROOTS.CLOUD_COMPASS}/cloud/oci/tenancies/${id}/${tab}`,
      },
    },
    reports: {
      root: `${ROOTS.CLOUD_COMPASS}/reports`,
      tabs: {
        root: `${ROOTS.CLOUD_COMPASS}/reports/list`,
      },
      consumption: {
        root: `${ROOTS.CLOUD_COMPASS}/reports/consumption`,
      },
      billing: {
        root: `${ROOTS.CLOUD_COMPASS}/reports/billing`,
      },
      payerAccount: {
        root: `${ROOTS.CLOUD_COMPASS}/reports/payer-account`,
      },
      approval: {
        root: `${ROOTS.CLOUD_COMPASS}/reports/approval`,
      },
    },
    resources: {
      root: `${ROOTS.CLOUD_COMPASS}/resources`,
      inventory: {
        root: `${ROOTS.CLOUD_COMPASS}/resources/inventory`,
      },
      tags: {
        root: `${ROOTS.CLOUD_COMPASS}/resources/tags`,
        new: `${ROOTS.CLOUD_COMPASS}/resources/tags/new`,
        edit: (id: string) => `${ROOTS.CLOUD_COMPASS}/resources/tags/${id}/edit`,
      },
    },
  },

  setting: {
    root: ROOTS.SETTING,
    general: {
      root: `${ROOTS.SETTING}/general`,
      countries: {
        root: `${ROOTS.SETTING}/general/countries`,
        new: `${ROOTS.SETTING}/general/countries/new`,
        edit: (id: string) => `${ROOTS.SETTING}/general/countries/${id}/edit`,
      },
      currencies: {
        root: `${ROOTS.SETTING}/general/currencies`,
        new: `${ROOTS.SETTING}/general/currencies/new`,
        edit: (id: string) => `${ROOTS.SETTING}/general/currencies/${id}/edit`,
      },
    },
    accounts: {
      root: `${ROOTS.SETTING}/accounts`,
      industries: {
        root: `${ROOTS.SETTING}/accounts/companies`,
        new: `${ROOTS.SETTING}/accounts/companies/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/companies/${id}/edit`,
      },
      segments: {
        root: `${ROOTS.SETTING}/accounts/companies/segments`,
        new: `${ROOTS.SETTING}/accounts/companies/segments/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/companies/segments/${id}/edit`,
      },
      lifeCycle: {
        root: `${ROOTS.SETTING}/accounts/companies/life-cycles`,
        new: `${ROOTS.SETTING}/accounts/companies/life-cycles/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/companies/life-cycles/${id}/edit`,
      },
      churnReason: {
        root: `${ROOTS.SETTING}/accounts/companies/churn-reasons`,
        new: `${ROOTS.SETTING}/accounts/companies/churn-reasons/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/companies/churn-reasons/${id}/edit`,
      },
      documentType: {
        root: `${ROOTS.SETTING}/accounts/companies/document-types`,
        new: `${ROOTS.SETTING}/accounts/companies/document-types/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/companies/document-types/${id}/edit`,
      },
      contactType: {
        root: `${ROOTS.SETTING}/accounts/companies/contact-types`,
        new: `${ROOTS.SETTING}/accounts/companies/contact-types/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/companies/contact-types/${id}/edit`,
      },
      contactStatus: {
        root: `${ROOTS.SETTING}/accounts/companies/contact-statuses`,
        new: `${ROOTS.SETTING}/accounts/companies/contact-statuses/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/companies/contact-statuses/${id}/edit`,
      },
      opportunities: {
        root: `${ROOTS.SETTING}/accounts/opportunities`,
        stages: {
          root: `${ROOTS.SETTING}/accounts/opportunities/stages`,
          new: `${ROOTS.SETTING}/accounts/opportunities/stages/new`,
          edit: (id: string) => `${ROOTS.SETTING}/accounts/opportunities/stages/${id}/edit`,
        },
      },
      presalesTicketStages: {
        root: `${ROOTS.SETTING}/accounts/presales-ticket-stages`,
        new: `${ROOTS.SETTING}/accounts/presales-ticket-stages/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/presales-ticket-stages/${id}/edit`,
      },
      presalesRequestTypes: {
        root: `${ROOTS.SETTING}/accounts/presales-request-types`,
        new: `${ROOTS.SETTING}/accounts/presales-request-types/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/presales-request-types/${id}/edit`,
      },
      presalesServiceTypes: {
        root: `${ROOTS.SETTING}/accounts/presales-service-types`,
        new: `${ROOTS.SETTING}/accounts/presales-service-types/new`,
        edit: (id: string) => `${ROOTS.SETTING}/accounts/presales-service-types/${id}/edit`,
      },
      presales: {
        root: `${ROOTS.SETTING}/accounts/presales`,
      },
    },

    approvalFlow: {
      root: `${ROOTS.SETTING}/approval-flow`,
      purchaseOrders: {
        root: `${ROOTS.SETTING}/approval-flow/purchase-orders`,
        new: `${ROOTS.SETTING}/approval-flow/purchase-orders/new`,
        edit: (id: string) => `${ROOTS.SETTING}/approval-flow/purchase-orders/${id}/edit`,
      },
      billingOrders: {
        root: `${ROOTS.SETTING}/approval-flow/billing-orders`,
        new: `${ROOTS.SETTING}/approval-flow/billing-orders/new`,
        edit: (id: string) => `${ROOTS.SETTING}/approval-flow/billing-orders/${id}/edit`,
      },
      newuser: {
        root: `${ROOTS.SETTING}/approval-flow/new-user`,
        new: `${ROOTS.SETTING}/approval-flow/new-user/new`,
        edit: (id: string) => `${ROOTS.SETTING}/approval-flow/new-user/${id}/edit`,
      },
    },

    portfolio: {
      root: `${ROOTS.SETTING}/portfolio`,
      productTypes: {
        root: `${ROOTS.SETTING}/portfolio/general`,
        new: `${ROOTS.SETTING}/portfolio/general/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/${id}/edit`,
      },
      businessUnits: {
        root: `${ROOTS.SETTING}/portfolio/general/business-units`,
        new: `${ROOTS.SETTING}/portfolio/general/business-units/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/business-units/${id}/edit`,
      },
      businessLines: {
        root: `${ROOTS.SETTING}/portfolio/general/business-lines`,
        new: `${ROOTS.SETTING}/portfolio/general/business-lines/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/business-lines/${id}/edit`,
      },
      productLines: {
        root: `${ROOTS.SETTING}/portfolio/general/product-lines`,
        new: `${ROOTS.SETTING}/portfolio/general/product-lines/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/product-lines/${id}/edit`,
      },
      studios: {
        root: `${ROOTS.SETTING}/portfolio/general/studios`,
        new: `${ROOTS.SETTING}/portfolio/general/studios/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/studios/${id}/edit`,
      },
      termConditions: {
        root: `${ROOTS.SETTING}/portfolio/general/term-conditions`,
        new: `${ROOTS.SETTING}/portfolio/general/term-conditions/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/term-conditions/${id}/edit`,
      },
      billingFrequency: {
        root: `${ROOTS.SETTING}/portfolio/general/billing-frequency`,
        new: `${ROOTS.SETTING}/portfolio/general/billing-frequency/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/billing-frequency/${id}/edit`,
      },
      externalityReason: {
        root: `${ROOTS.SETTING}/portfolio/general/externality-reasons`,
        new: `${ROOTS.SETTING}/portfolio/general/externality-reasons/new`,
        edit: (id: string) => `${ROOTS.SETTING}/portfolio/general/externality-reasons/${id}/edit`,
      },
    },
    catalogSections: {
      root: `${ROOTS.SETTING}/catalog-sections`,
      list: `${ROOTS.SETTING}/catalog-sections/list`,
      new: `${ROOTS.SETTING}/catalog-sections/new`,
      edit: (id: string) => `${ROOTS.SETTING}/catalog-sections/${id}/edit`,
      partnerSections: {
        root: `${ROOTS.SETTING}/catalog-sections/partners`,
      },
      productSections: {
        root: `${ROOTS.SETTING}/catalog-sections/products`,
      },
    },
    finances: {
      root: `${ROOTS.SETTING}/finances`,
      payments: {
        root: `${ROOTS.SETTING}/finances/payments`,
        new: `${ROOTS.SETTING}/finances/payments/new`,
        edit: (id: string) => `${ROOTS.SETTING}/finances/payments/${id}/edit`,
      },
    },
    access: {
      root: `${ROOTS.SETTING}/access`,
      logs: {
        root: `${ROOTS.SETTING}/access/logs`,
        detail: (id: string) => `${ROOTS.SETTING}/access/logs/${id}`,
      },
      featureFlags: {
        root: `${ROOTS.SETTING}/access/feature-flags`,
        new: `${ROOTS.SETTING}/access/feature-flags/new`,
        edit: (id: string) => `${ROOTS.SETTING}/access/feature-flags/${id}/edit`,
      },
    },
    integrations: {
      root: `${ROOTS.SETTING}/integrations`,
      odooSync: {
        root: `${ROOTS.SETTING}/integrations/odoo-sync`,
      },
      llms: {
        root: `${ROOTS.SETTING}/integrations/llms`,
      },
    },
    directory: {
      root: `${ROOTS.SETTING}/directory`,
      user: {
        root: `${ROOTS.SETTING}/directory/users`,
        new: `${ROOTS.SETTING}/directory/users/new`,
        edit: (id: string) => `${ROOTS.SETTING}/directory/users/${id}/edit`,
      },
      usermkt: {
        root: `${ROOTS.SETTING}/directory/us-mkt`,
        new: `${ROOTS.SETTING}/directory/us-mkt/new`,
        edit: (id: string) => `${ROOTS.SETTING}/directory/us-mkt/${id}/edit`,
      },
      teams: {
        root: `${ROOTS.SETTING}/directory/teams`,
        new: `${ROOTS.SETTING}/directory/teams/new`,
        edit: (id: string) => `${ROOTS.SETTING}/directory/teams/${id}/edit`,
      },
      roles: {
        root: `${ROOTS.SETTING}/directory/roles`,
        new: `${ROOTS.SETTING}/directory/roles/new`,
        edit: (id: string) => `${ROOTS.SETTING}/directory/roles/${id}/edit`,
      },
      banks: {
        root: `${ROOTS.SETTING}/directory/bank`,
        new: `${ROOTS.SETTING}/directory/bank/new`,
        edit: (id: string) => `${ROOTS.SETTING}/directory/bank/${id}/edit`,
      },
    },
  },
  // LEGAL
  legal: {
    root: ROOTS.LEGAL,
    privacyPolicy: `${ROOTS.LEGAL}/privacy-policy`,
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    one: `${ROOTS.DASHBOARD}/one`,
  },
};
